import React from 'react';
import { usageSegments, type UsageLevel } from '../lib/usage';

// The maths lives in `lib/usage.ts` (pinned by a Node test); re-exported here
// so `@shared`'s public names do not move.
export { usageOverCap } from '../lib/usage';
export type { UsageLevel } from '../lib/usage';

export interface UsageBarProps {
    /** Filled share, 0–100. `null` = no cap: an empty track, never a fill. */
    pct: number | null;
    level: UsageLevel;
    /** Share of the cap that something NOT YET committed would add (what a
     *  workshop is about to upload, import…), drawn hatched right after the
     *  used part. When `pct + pendingPct > 100` the pending part turns red,
     *  fills the rest of the track (never less than `MIN_OVER_PCT`, taken from
     *  the used part, so a full bar still shows it) and the track gets a red
     *  ring — it never pretends to exceed 100 %; the caller's label says by
     *  how much (see `StorageUsageBar`). Without a pending part nothing
     *  changes: a `pct` above 100 is a plain full bar, as before. */
    pendingPct?: number | null;
    /** Colour of the pending part while it fits. Default: `level`. Pass the
     *  level of the PROJECTED total, so the hatch already warns before it
     *  lands. Ignored when over the cap (always red). */
    pendingLevel?: UsageLevel;
    /** Over-cap decision made by the caller in its own exact units (bytes),
     *  overriding the one derived from the two shares — which can disagree at
     *  exactly the cap by a float rounding. `StorageUsageBar` passes it, so its
     *  bar and its label never disagree. Ignored without a pending part. */
    overCap?: boolean;
    /** Track thickness: `sm` (h-2, admin rows) | `md` (h-3, default). */
    size?: 'sm' | 'md';
    /** `aria-valuetext` — the human reading of the meter (the caller's label). */
    valueText?: string;
    className?: string;
}

const FILL: Record<UsageLevel, string> = {
    ok: 'bg-emerald-500',
    warn: 'bg-amber-500',
    urgent: 'bg-orange-500',
    full: 'bg-red-500',
};

/** The hatch is drawn with `currentColor`, so the pending colour is a text colour. */
const HATCH_TEXT: Record<UsageLevel, string> = {
    ok: 'text-emerald-500',
    warn: 'text-amber-500',
    urgent: 'text-orange-500',
    full: 'text-red-500',
};

const HATCH_STYLE: React.CSSProperties = {
    backgroundImage: 'repeating-linear-gradient(135deg, currentColor 0 3px, transparent 3px 6px)',
};

/**
 * The usage track of a capped resource: storage (`StorageUsageBar`) and the
 * monthly invoice quota (tenant Configuración → Facturación). Pure
 * presentational — the caller computes the share and the level.
 */
const UsageBar: React.FC<UsageBarProps> = ({
    pct,
    level,
    pendingPct,
    pendingLevel,
    overCap,
    size = 'md',
    valueText,
    className = '',
}) => {
    // The pending part starts where the used part ends and stops at the cap.
    const { used: width, pending: pendingWidth, over } = usageSegments(pct, pendingPct, overCap);
    const hasPending = pendingWidth > 0;
    const trackH = size === 'sm' ? 'h-2' : 'h-3';
    return (
        <div
            // A bar without a pending part keeps the exact class it always had;
            // `flex` (two segments side by side) and the ring only come with it.
            className={`${trackH} ${hasPending ? 'flex ' : ''}rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden${
                over ? ' ring-1 ring-red-500/60' : ''
            } ${className}`}
            role="meter"
            aria-valuemin={0}
            aria-valuemax={100}
            // The real share, not the drawn width (over the cap the used part
            // gives room to the red part).
            aria-valuenow={pct == null ? undefined : Math.round(Math.max(0, Math.min(100, pct)))}
            aria-valuetext={valueText}
        >
            {pct != null && (
                <div
                    className={`h-full ${hasPending ? '' : 'rounded-full'} transition-all ${FILL[level]}`}
                    style={{ width: `${width}%` }}
                />
            )}
            {hasPending && (
                <div
                    data-pending=""
                    className={`h-full transition-all ${HATCH_TEXT[over ? 'full' : (pendingLevel ?? level)]} ${
                        over ? 'opacity-90' : 'opacity-70'
                    }`}
                    style={{ ...HATCH_STYLE, width: `${pendingWidth}%` }}
                />
            )}
        </div>
    );
};

export default UsageBar;
